import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { metadataService, tagService } from "../services/api";
import { BaseContext } from "./BaseContext";
import { graphService } from "../services/api";
import { toast } from "react-hot-toast";
import { findNodeStats } from "../components/MainContent/Workflows/screens/DataDiscovery/Graph/GraphNodeUtils";

export const GraphContext = createContext();

export const GraphProvider = ({ children }) => {
  const [graphData, setGraphData] = useState(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [hierarchyStats, setHierarchyStats] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [activeDragNode, setActiveDragNode] = useState(null);
  const [activeOverlappingNode, setActiveOverlappingNode] = useState(null);
  const [graphs, setGraphs] = useState([]);
  const [shouldFitView, setShouldFitView] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [showNodesWithNoValue, setShowNodesWithNoValue] = useState(true);
  const [graphLayoutDirection, setGraphLayoutDirection] =
    useState("horizontal");
  const [highlightedLevel, setHighlightedLevel] = useState(null);
  const {
    deasyUserConfig,
    deasyApiKey,
    vdbLastActiveProfileName,
    vdbFilesCount,
    selectedDataSlice,
  } = useContext(BaseContext);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [loadingUniqueTags, setLoadingUniqueTags] = useState(false);
  const [tagScores, setTagScores] = useState({});
  const [loadingTagScores, setLoadingTagScores] = useState(false);
  const [loadingGraphs, setLoadingGraphs] = useState(false);
  const [isRefreshingHierarchy, setIsRefreshingHierarchy] = useState(false);

  // View mode states for Enhanced DAG Visualization - Default to tag-tag as primary mode
  const [graphViewMode, setGraphViewMode] = useState(
    localStorage.getItem("graphViewMode") || "tag-tag",
  ); // "tag-value" or "tag-tag"
  const [expandedTagNodes, setExpandedTagNodes] = useState(new Set()); // Track which TagNodes are expanded

  // Bundle hover highlighting state
  const [hoveredBundleId, setHoveredBundleId] = useState(null);

  // Keep track of initial load
  const [initialLoad, setInitialLoad] = useState(true);
  const [isViewCentered, setIsViewCentered] = useState(false);

  // When component mounts, trigger animation immediately
  useEffect(() => {
    if (initialLoad) {
      // Remove automatic view setting - let individual components control this
      // setShouldFitView(true);
      setInitialLoad(false);
    }
  }, [initialLoad]);

  const [selectedDiscoveryGraph, setSelectedDiscoveryGraph] = useState(null);
  const discoveryGraphData = useMemo(
    () => selectedDiscoveryGraph?.graph_data || {},
    [selectedDiscoveryGraph?.graph_data],
  );

  // When graph changes, trigger animation
  const handleGraphChange = useCallback((graph) => {
    setSelectedDiscoveryGraph(graph);
    // Remove automatic view setting - let individual components control this
    // setShouldFitView(true);
    setIsViewCentered(false); // Reset centered state on graph change
  }, []);

  const setDiscoveryGraphData = (data) => {
    setSelectedDiscoveryGraph((prev) => ({
      ...prev,
      graph_data: data,
    }));
  };

  const GRAPH_PANEL_MODES = Object.freeze({
    HOME: "home",
    MANUAL: "manual",
    LIBRARY: "library",
    AUTO: "auto",
  });

  const [selectedMode, setSelectedMode] = useState(GRAPH_PANEL_MODES.HOME);

  // States for undo/redo functionality
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false);
  const [isAtomicUpdateInProgress, setIsAtomicUpdateInProgress] =
    useState(false);

  // Function to extract all tag names from the discovery graph data
  const extractTagNames = useCallback((data) => {
    const tagNames = new Set();

    const traverse = (obj) => {
      if (!obj || typeof obj !== "object") return;

      Object.entries(obj).forEach(([key, value]) => {
        // Check if this is a tag node (has TagAvailableValues property)
        if (key !== "TagAvailableValues" && value?.TagAvailableValues) {
          tagNames.add(key);
        }

        // Continue traversing if value is an object
        if (typeof value === "object" && value !== null) {
          traverse(value);
        }
      });
    };

    traverse(data);
    return Array.from(tagNames);
  }, []);

  // Function to fetch scores for all tags in the graph
  const fetchAllTagScores = async () => {
    if (loadingTagScores || !deasyApiKey) return;

    const tagNames = extractTagNames(discoveryGraphData);
    if (tagNames.length === 0) return;

    try {
      setLoadingTagScores(true);

      const response = await tagService.getTagsScores(
        tagNames,
        vdbLastActiveProfileName,
        deasyApiKey,
        selectedDataSlice?.id || null,
      );

      if (response.data?.scores) {
        setTagScores(response.data.scores);
      }
    } catch (error) {
      console.error("Error fetching tag scores:", error);
    } finally {
      setLoadingTagScores(false);
    }
  };

  // Fetch tag scores whenever the graph data changes
  useEffect(
    () => {
      const fetchInitialData = async () => {
        if (Object.keys(discoveryGraphData).length > 0) {
          await fetchAllTagScores();
        }
        await fetchGraphs();
      };
      fetchInitialData();
    },
    // eslint-disable-next-line
    [],
  );

  const saveToHistory = useCallback(
    (graphData) => {
      const stateToSave = JSON.parse(
        JSON.stringify(graphData || selectedDiscoveryGraph?.graph_data || {}),
      );

      setUndoStack((prevStack) => {
        if (prevStack.length > 0) {
          // eslint-disable-next-line no-unused-vars
          const lastState = prevStack[prevStack.length - 1];
        }

        const newStack = [...prevStack];
        if (newStack.length >= 20) newStack.shift();
        newStack.push(stateToSave);
        return newStack;
      });

      setRedoStack([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDiscoveryGraph],
  );

  useEffect(() => {
    // Reset undo/redo stacks when switching graphs
    setUndoStack([]);
    setRedoStack([]);
  }, [selectedDiscoveryGraph?.graph_id]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;

    setIsUndoRedoOperation(true);

    setRedoStack((prevRedoStack) => {
      const currentState = JSON.parse(JSON.stringify(discoveryGraphData));
      return [...prevRedoStack, currentState];
    });

    setUndoStack((prevUndoStack) => {
      const newUndoStack = [...prevUndoStack];
      const previousState = newUndoStack.pop();
      setDiscoveryGraphData(previousState);
      return newUndoStack;
    });

    setTimeout(() => {
      setIsUndoRedoOperation(false);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoStack, discoveryGraphData]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;

    setIsUndoRedoOperation(true);

    setUndoStack((prevUndoStack) => {
      const currentState = JSON.parse(JSON.stringify(discoveryGraphData));
      const lastState = prevUndoStack[prevUndoStack.length - 1];

      if (
        lastState &&
        JSON.stringify(lastState) === JSON.stringify(currentState)
      ) {
        return prevUndoStack;
      }

      return [...prevUndoStack, currentState];
    });

    setRedoStack((prevRedoStack) => {
      const newRedoStack = [...prevRedoStack];
      const nextState = newRedoStack.pop();

      setDiscoveryGraphData(nextState);

      return newRedoStack;
    });

    setTimeout(() => {
      setIsUndoRedoOperation(false);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redoStack, discoveryGraphData, undoStack]);

  const handleSaveGraph = async (graphData) => {
    if (!graphData) {
      graphData = selectedDiscoveryGraph;
    }
    if (!graphData) return;

    try {
      await graphService.createUpdateGraph(
        deasyApiKey,
        graphData.graph_name,
        graphData.graph_description,
        graphData.graph_data,
      );
      // Refresh graph list after save
      const updatedGraphs = await graphService.getGraphs(deasyApiKey);
      setGraphs(updatedGraphs);
    } catch (error) {
      console.error("Failed to save graph:", error);
      toast.error("Failed to save graph changes");
    }
  };

  const updateDiscoveryGraphData = useCallback(
    (newData) => {
      // Validate the new data
      if (newData === null || newData === undefined) {
        console.warn("Attempted to set null/undefined discoveryGraphData");
        return;
      }

      if (typeof newData !== "object") {
        console.warn(
          "Attempted to set non-object discoveryGraphData:",
          typeof newData,
        );
        return;
      }
      if (!isUndoRedoOperation && !isAtomicUpdateInProgress) {
        const currentState = JSON.parse(
          JSON.stringify(discoveryGraphData || {}),
        );
        saveToHistory(currentState);
      }
      setDiscoveryGraphData(newData);
    },
    [
      discoveryGraphData,
      saveToHistory,
      isUndoRedoOperation,
      isAtomicUpdateInProgress,
    ],
  );

  const refreshHierarchyStats = async (apiKey) => {
    try {
      if (!deasyApiKey) {
        return;
      }

      setIsRefreshingHierarchy(true);

      const requestData = {
        data_connector_name: vdbLastActiveProfileName,
        endpoint_manager_config:
          deasyUserConfig.llmConfig?.Configs?.[
            deasyUserConfig.llmConfig?.LastActive
          ],
        current_tree: JSON.stringify(discoveryGraphData) || [],
        totalFiles: vdbFilesCount?.total_files || null,
        dataslice_id: selectedDataSlice?.id || null,
      };

      fetchAllTagScores();
      const hierarchyResponse =
        await metadataService.getHierarchyCountDistributions(
          requestData,
          apiKey,
        );

      // Store previous stats for comparison
      const previousStats = hierarchyStats;
      const newStats = hierarchyResponse.data?.hierarchy;

      // Find updated nodes by comparing hierarchies
      const findUpdatedNodes = (oldNode, newNode, path = []) => {
        const updates = [];

        if (!oldNode || !newNode) return updates;

        // Check if current node was updated
        if (
          oldNode.file_count !== newNode.file_count ||
          oldNode.percentage !== newNode.percentage
        ) {
          updates.push({
            path: [...path],
            name: newNode.name,
            oldCount: oldNode.file_count,
            newCount: newNode.file_count,
            oldPercentage: oldNode.percentage,
            newPercentage: newNode.percentage,
          });
        }

        // Recursively check children
        if (newNode.children) {
          newNode.children.forEach((newChild) => {
            const oldChild = oldNode.children?.find(
              (c) => c.name === newChild.name,
            );
            updates.push(
              ...findUpdatedNodes(oldChild, newChild, [...path, newChild.name]),
            );
          });
        }

        return updates;
      };

      const updatedNodes = findUpdatedNodes(previousStats, newStats);

      setHierarchyStats(newStats);
      return updatedNodes; // Return the updates if needed elsewhere
    } catch (error) {
      console.error("Error refreshing hierarchy stats:", error);
      return [];
    } finally {
      setIsRefreshingHierarchy(false);
    }
  };

  const startAtomicUpdate = useCallback(() => {
    setIsAtomicUpdateInProgress(true);
    return () => setIsAtomicUpdateInProgress(false);
  }, []);

  const fetchGraphs = async () => {
    // Skip if already loading to prevent double calls
    if (loadingGraphs) {
      return;
    }
    // Skip if no API key
    if (!deasyApiKey) {
      console.log("No API key available, skipping graph fetch");
      return;
    }
    try {
      setLoadingGraphs(true);
      console.log("Fetching graphs...");
      const fetchedGraphs = await graphService.getGraphs(deasyApiKey);
      console.log(`Fetch successful, found ${fetchedGraphs.length} graphs`);

      // Set graphs regardless of result (even if empty)
      setGraphs(fetchedGraphs);

      // If no graphs exist, create a default one
      if (fetchedGraphs.length === 0) {
        const defaultGraph = {
          graph_name: "Default Schema",
          graph_description: "Default schema created automatically",
          graph_data: {},
        };

        // Create the default graph
        await graphService.createUpdateGraph(
          deasyApiKey,
          defaultGraph.graph_name,
          defaultGraph.graph_description,
          defaultGraph.graph_data,
        );

        // Fetch again to get the newly created graph
        const updatedGraphs = await graphService.getGraphs(deasyApiKey);
        setGraphs(updatedGraphs);

        // Select the newly created default graph
        const newDefaultGraph = updatedGraphs.find(
          (g) => g.graph_name === defaultGraph.graph_name,
        );
        setSelectedDiscoveryGraph(newDefaultGraph);
        setDiscoveryGraphData(newDefaultGraph?.graph_data || {});

        if (newDefaultGraph?.graph_id) {
          localStorage.setItem("lastActiveGraphId", newDefaultGraph.graph_id);
        }

        setLoadingGraphs(false);
        return;
      }

      const lastActiveGraphId = localStorage.getItem("lastActiveGraphId");

      let graphToSelect = null;

      if (lastActiveGraphId) {
        graphToSelect = fetchedGraphs.find(
          (g) => g.graph_id === lastActiveGraphId,
        );
      }

      if (!graphToSelect) {
        graphToSelect = selectedDiscoveryGraph
          ? fetchedGraphs.find(
              (g) => g.graph_id === selectedDiscoveryGraph.graph_id,
            ) || fetchedGraphs[0]
          : fetchedGraphs[0];
      }

      setSelectedDiscoveryGraph(graphToSelect);
      setDiscoveryGraphData(graphToSelect?.graph_data || {});
    } catch (error) {
    } finally {
      setLoadingGraphs(false);
    }
  };

  const fetchUniqueTags = useCallback(async () => {
    if (loadingUniqueTags) {
      console.log("Already loading unique tags, skipping duplicate call");
      return;
    }

    if (!deasyApiKey) {
      console.log("Missing API key, skipping unique tags fetch");
      return;
    }

    const activeVdbConfig =
      deasyUserConfig.vdbmConfig?.Configs?.[vdbLastActiveProfileName];

    if (!activeVdbConfig) {
      console.log("No active VDB config, skipping unique tags fetch");
      return;
    }

    try {
      setLoadingUniqueTags(true);

      const response = await metadataService.getUniqueTags(
        deasyApiKey,
        null, // fileNames
        vdbLastActiveProfileName,
        null, // dataslice_id
        null, // node_condition
      );

      if (response.data?.tags) {
        setUniqueTags(response.data.tags);
      }
    } catch (error) {
      console.error("Error fetching unique tags:", error);
    } finally {
      setLoadingUniqueTags(false);
    }
  }, [
    loadingUniqueTags,
    deasyApiKey,
    deasyUserConfig.vdbmConfig?.Configs,
    vdbLastActiveProfileName,
  ]);

  const tagNodes = useMemo(() => {
    if (!discoveryGraphData) return [];

    const tagOptions = [];

    const extractTagNodes = (obj, currentPath = [], depth = 0) => {
      if (!obj || typeof obj !== "object") return;

      const isQuestion = depth % 2 === 0;

      if (isQuestion) {
        Object.entries(obj).forEach(([key, value]) => {
          if (key !== "TagAvailableValues" && typeof value === "object") {
            const path = [...currentPath, key];

            // Generate node ID based on path to match the actual graph nodes
            // This ensures consistency with how nodes are identified in the graph
            const nodeId = path.join("_").replace(/\s+/g, "_").toLowerCase();

            tagOptions.push({
              value: nodeId,
              label: key,
              path,
              nodeId: nodeId, // Keep original nodeId for reference
            });

            if (value && typeof value === "object") {
              extractTagNodes(value, path, depth + 1);
            }
          }
        });
      } else {
        if (obj.TagAvailableValues !== undefined) {
          const availableValues = Array.isArray(obj.TagAvailableValues)
            ? obj.TagAvailableValues
            : [];

          let values = [
            ...new Set([
              ...Object.keys(obj).filter((k) => k !== "TagAvailableValues"),
              ...availableValues,
            ]),
          ].sort();

          const nodeStats = findNodeStats(hierarchyStats, currentPath);

          if (!showNodesWithNoValue && hierarchyStats) {
            values = values.filter((value) => {
              const validCount = nodeStats?.children?.some(
                (stat) => stat.name === value && stat.file_count > 0,
              );
              const valueIsNotFound = value === "Not found";
              return validCount || valueIsNotFound;
            });
          }

          values.push("Not found");

          values.forEach((value) => {
            const valuePath = [...currentPath, value];

            if (value in obj) {
              extractTagNodes(obj[value], valuePath, depth + 1);
            }
          });
        }
      }
    };

    extractTagNodes(discoveryGraphData, [], 0);
    return tagOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryGraphData, showNodesWithNoValue, hierarchyStats]);

  const contextValue = {
    graphData,
    setGraphData,
    sidePanelOpen,
    setSidePanelOpen,
    selectedNodeData,
    setSelectedNodeData,
    hierarchyStats,
    setHierarchyStats,
    selectedNodes,
    setSelectedNodes,
    activeDragNode,
    setActiveDragNode,
    activeOverlappingNode,
    setActiveOverlappingNode,
    discoveryGraphData,
    updateDiscoveryGraphData,
    graphs,
    setGraphs,
    fetchGraphs,
    selectedDiscoveryGraph,
    setSelectedDiscoveryGraph: handleGraphChange,
    handleSaveGraph,
    shouldFitView,
    setShouldFitView,
    selectedMode,
    setSelectedMode,
    refreshHierarchyStats,
    isRefreshingHierarchy,
    GRAPH_PANEL_MODES,
    handleUndo,
    handleRedo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    isAtomicUpdateInProgress,
    startAtomicUpdate,
    uniqueTags,
    loadingUniqueTags,
    fetchUniqueTags,
    tagScores,
    loadingTagScores,
    fetchAllTagScores,
    focusedNodeId,
    setFocusedNodeId,
    tagNodes,
    loadingGraphs,
    isViewCentered,
    setIsViewCentered,
    showNodesWithNoValue,
    setShowNodesWithNoValue,
    graphLayoutDirection,
    setGraphLayoutDirection,
    highlightedLevel,
    setHighlightedLevel,
    // Enhanced DAG Visualization
    graphViewMode,
    setGraphViewMode,
    expandedTagNodes,
    setExpandedTagNodes,
    // Bundle hover highlighting state
    hoveredBundleId,
    setHoveredBundleId,
  };

  return (
    <GraphContext.Provider value={contextValue}>
      {children}
    </GraphContext.Provider>
  );
};

export const useGraph = () => {
  const context = useContext(GraphContext);
  if (!context) {
    throw new Error("useGraph must be used within a GraphProvider");
  }
  return context;
};
